import { useQuery } from "@tanstack/react-query";

import { api } from "../../shared/api/client";
import type { PointRecord, Student } from "../../shared/api/types";
import { DESIGN_CUTE_PATH, designCuteAsset, image } from "../../shared/assets/manifest";
import { growthInfo } from "../../shared/growth";
import { EmptyState } from "../../shared/ui/EmptyState";
import { LoadingBlock } from "../../shared/ui/LoadingBlock";
import { CATEGORY_META } from "../dashboard/actions";

type SharePayload = {
  className: string;
  student: Student;
  recentHistory: PointRecord[];
  badges?: Array<{ id?: string; icon?: string; name: string; description?: string }>;
  growthLimits?: number[];
};

function assetIcon(icon?: string) {
  const value = icon || "";
  if (value.startsWith("design-cute/") && value.endsWith(".png")) {
    return designCuteAsset(value.substring("design-cute/".length));
  }
  if (value.startsWith(DESIGN_CUTE_PATH) && value.endsWith(".png")) return value;
  return image.trophy;
}

function categoryLabel(category?: string) {
  return CATEGORY_META[category || "manual"]?.label || "自定义";
}

export function SharePage() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const share = useQuery({
    queryKey: ["share", token],
    enabled: Boolean(token),
    queryFn: async () => api<SharePayload>(`/api/public/share/${encodeURIComponent(token)}`),
    select: (payload) => payload.data as SharePayload
  });

  if (!token) {
    return (
      <main className="share-page">
        <EmptyState title="分享链接无效" />
      </main>
    );
  }
  if (share.isLoading) {
    return (
      <main className="share-page">
        <LoadingBlock />
      </main>
    );
  }
  if (!share.data) {
    return (
      <main className="share-page">
        <EmptyState title="无法打开分享链接" detail={share.error instanceof Error ? share.error.message : ""} />
      </main>
    );
  }

  const { student, className, recentHistory, badges } = share.data;
  const growth = growthInfo(student, share.data.growthLimits);

  return (
    <main className="share-page">
      <header className="share-header">
        <div className="brand-lockup">
          <img src={image.star} alt="" />
          <strong>
            班级积分管理系统
            <small>学生成长分享</small>
          </strong>
          <img className="brand-rainbow" src={image.flagRainbow} alt="" />
        </div>
        <div>
          <h1>{className}</h1>
          <p>
            学生 · {student.groupName || "未分组"}
          </p>
        </div>
      </header>

      <section className="share-card share-hero">
        <span className="share-ribbon">个人成长</span>
        <img className="share-laurel" src={image.laurel} alt="" />
        <div className="share-profile">
          <img className="share-avatar share-avatar--growth" src={growth.imageSrc} alt="" title={growth.label} />
          <div>
            <h2>{student.name}</h2>
            <p>{student.groupName || "未分组"}</p>
          </div>
        </div>
        <div className="share-points">
          <span>当前总积分</span>
          <strong>{student.points}</strong>
        </div>
      </section>

      <section className="surface-panel">
        <div className="panel-heading">
          <h2>成长状态</h2>
          <span>{growth.label}</span>
        </div>
        <div className="share-growth-row">
          <img src={growth.imageSrc} alt="" />
          <div className="share-growth-meta">
            <strong>{growth.label}</strong>
            <div className="share-progress">
              <div className="share-progress-fill" style={{ width: `${growth.progress}%` }} />
            </div>
            <span>成长值 {growth.progress}%</span>
          </div>
        </div>
      </section>

      {badges?.length ? (
        <section className="surface-panel">
          <div className="panel-heading">
            <h2>徽章</h2>
          </div>
          <div className="badge-strip">
            {badges.map((badge) => (
              <span className="badge-chip" key={badge.id || badge.name} title={badge.description || badge.name}>
                <img src={assetIcon(badge.icon)} alt="" />
                {badge.name}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <section className="surface-panel">
        <div className="panel-heading">
          <h2>最近记录</h2>
        </div>
        {recentHistory.length ? (
          <div className="notebook-list">
            {recentHistory.map((record) => (
              <article className="notebook-row" key={record.id}>
                <strong>{categoryLabel(record.category)}</strong>
                <span>{record.reason || "记分"}</span>
                <b>{Number(record.amount || 0) > 0 ? `+${record.amount}` : record.amount}</b>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState title="暂无记录" />
        )}
      </section>
    </main>
  );
}
